import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogConfig,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { EDialogWidth } from '../../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../../core/validators/trimmed-required.validator';
import { ErrorMessagesComponent } from '../../../../../shared/components/error-messages/error-messages.component';
import { IDropdownOption, MatDropdown } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { FormUtilitiesService } from '../../../../../utilities/form-utilities.service';
import { ESnackbar, SnackbarService } from '../../../../../utilities/snackbar.service';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { BehaviorSubject, startWith } from 'rxjs';
import { IFormAutofillMapping } from 'scw-digital-boards-widgets';
import { ERestrictAccessBy } from '../../widget-requests/widget-requests-add-edit-dialog/widget-requests-add-edit-dialog.interface';
import { IFormAutofillAddPayload, IFormAutofillAuthorization } from '../form-autofill.interface';
import { IFormAutofillAddEditDialogData } from './form-autofill-add-edit-dialog.interface';
import { FormAutofillAddEditDialogStore } from './form-autofill-add-edit-dialog.store';
import { FormAutofillMappingComponent } from './form-autofill-mapping/form-autofill-mapping.component';

@Component({
  imports: [
    ErrorMessagesComponent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    TranslatePipe,
    MatDropdown,
    FormAutofillMappingComponent,
    MatIcon,
  ],
  providers: [FormAutofillAddEditDialogStore],
  selector: 'app-form-autofill-add-edit-dialog',
  templateUrl: './form-autofill-add-edit-dialog.component.html',
})
export class FormAutofillAddEditDialogComponent implements OnInit {
  public readonly injectData: IFormAutofillAddEditDialogData = inject<IFormAutofillAddEditDialogData>(MAT_DIALOG_DATA);
  public readonly store: FormAutofillAddEditDialogStore = inject(FormAutofillAddEditDialogStore);
  public readonly dashboardSearch: BehaviorSubject<string> = new BehaviorSubject('');
  public readonly workspaceSearch: BehaviorSubject<string> = new BehaviorSubject('');
  public readonly ERestrictAccessBy: typeof ERestrictAccessBy = ERestrictAccessBy;

  private readonly matDialogRef: MatDialogRef<any, any> = inject(MatDialogRef);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly snackbarService: SnackbarService = inject(SnackbarService);

  public static dialogConfig: MatDialogConfig = {
    closeOnNavigation: true,
    disableClose: true,
    exitAnimationDuration: 0,
    maxWidth: '100%',
    width: EDialogWidth.medium,
  };

  public readonly restrictAccessByDropdownOptions: IDropdownOption<ERestrictAccessBy>[] = [
    {
      id: ERestrictAccessBy.none,
      name: this.translate.instant('field.noRestriction'),
    },
    {
      id: ERestrictAccessBy.workspace,
      name: this.translate.instant('field.workspace'),
    },
    {
      id: ERestrictAccessBy.dashboard,
      name: this.translate.instant('field.dashboard'),
    },
  ];

  public readonly addEditForm = new FormGroup({
    dashboardId: new FormControl<IDropdownOption[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    form: new FormControl<IDropdownOption[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    formAutofillMappings: new FormControl<IFormAutofillMapping[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(50)],
    }),
    restrictAccessBy: new FormControl<IDropdownOption<ERestrictAccessBy>[]>([this.restrictAccessByDropdownOptions[0]], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    workspaceId: new FormControl<IDropdownOption[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  public readonly formDropdownSettings: IDropdownSettings = {
    enableSearchFilter: true,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.form'),
  };

  public readonly dashboardDropdownSettings: IDropdownSettings = {
    enableSearchFilter: true,
    hasDelayedDefaultValue: true,
    isClientSide: false,
    repositionDropdownList: true,
    singleSelection: false,
    text: this.translate.instant('field.dashboard'),
  };

  public readonly restrictAccessByDropdownSettings: IDropdownSettings = {
    enableSearchFilter: true,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.restrictAccessBy'),
  };

  public readonly workspaceDropdownSettings: IDropdownSettings = {
    enableSearchFilter: true,
    hasDelayedDefaultValue: true,
    isClientSide: false,
    repositionDropdownList: true,
    singleSelection: false,
    text: this.translate.instant('field.workspace'),
  };

  constructor() {
    this.initEditData();
    this.initSearchSubscribers();
    this.handleAccessValidation();

    this.store.afterCreateUpdateSuccess$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
      this.matDialogRef.close();
    });
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      return;
    }

    const payload = this.formatOnePayload();

    if (this.injectData.editData?.id) {
      this.store.updateOne({ dto: payload, id: this.injectData.editData.id });
    } else {
      this.store.createOne(payload);
    }
  }

  public ngOnInit(): void {
    this.store.loadForms({ limit: 1000 });
    this.store.loadWidgetRequests({ filters: [{ field: 'requestMethod', ids: ['GET'] }], limit: 1000 });

    this.addEditForm.controls.form.valueChanges.subscribe((form) => {
      this.store.loadFormFields(form[0].id);
    });
  }

  private initEditData(): void {
    const { editData } = this.injectData;

    if (!editData) {
      return;
    }

    const auth: IFormAutofillAuthorization | null = editData.authorization;
    const scope: ERestrictAccessBy = auth?.scope ?? ERestrictAccessBy.none;

    this.addEditForm.reset({
      dashboardId: scope === ERestrictAccessBy.dashboard ? auth!.ids.map((id) => ({ id, name: '...' })) : [],
      form: [{ id: editData.formId, name: '...' }],
      formAutofillMappings: editData.formAutofillMappings,
      name: editData.name,
      restrictAccessBy: this.restrictAccessByDropdownOptions.filter((opt) => opt.id === scope),
      workspaceId: scope === ERestrictAccessBy.workspace ? auth!.ids.map((id) => ({ id, name: '...' })) : [],
    });
  }

  private initSearchSubscribers(): void {
    this.dashboardSearch.pipe(takeUntilDestroyed()).subscribe((search) => {
      this.store.loadDashboards({
        ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
      });
    });

    this.workspaceSearch.pipe(takeUntilDestroyed()).subscribe((search) => {
      this.store.loadWorkspaces({
        ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
      });
    });
  }

  private handleAccessValidation(): void {
    this.addEditForm.controls.restrictAccessBy.valueChanges
      .pipe(startWith(this.addEditForm.controls.restrictAccessBy.value), takeUntilDestroyed())
      .subscribe((val) => {
        const selectedId: ERestrictAccessBy | undefined = val?.[0]?.id;

        if (!selectedId || selectedId === ERestrictAccessBy.none) {
          this.addEditForm.controls.dashboardId.disable();
          this.addEditForm.controls.workspaceId.disable();
        } else if (selectedId === ERestrictAccessBy.dashboard) {
          this.addEditForm.controls.dashboardId.enable();
          this.addEditForm.controls.workspaceId.disable();
        } else {
          this.addEditForm.controls.dashboardId.disable();
          this.addEditForm.controls.workspaceId.enable();
        }
      });
  }

  private formatOnePayload(): IFormAutofillAddPayload {
    const { value } = this.addEditForm;

    return {
      authorization: this.getAuthorizationPayload(),
      formAutofillMappings: value?.formAutofillMappings ?? null,
      formId: value.form![0].id,
      name: FormUtilitiesService.trimIfString(value.name!),
    };
  }

  private getAuthorizationPayload(): IFormAutofillAddPayload['authorization'] {
    const { restrictAccessBy, workspaceId, dashboardId } = this.addEditForm.getRawValue();
    const restrictAccessById: ERestrictAccessBy | undefined = restrictAccessBy?.[0]?.id;

    if (!restrictAccessById || restrictAccessById === ERestrictAccessBy.none) {
      return null;
    }

    const isWorkspace: boolean = restrictAccessById === ERestrictAccessBy.workspace;
    const selectedIds: IDropdownOption[] = isWorkspace ? workspaceId : dashboardId;

    return {
      ids: (selectedIds || []).map((option: IDropdownOption) => option.id),
      scope: restrictAccessById,
    };
  }
}
